/**
 * useDataVersion — cheap polling that fires a refetch callback whenever the
 * server-side data-version for a specific dashboard `kind` changes.
 *
 * Server: GET /api/data-version → { "invoice-pi": iso, "solar-project": iso, ... }
 * Polling interval: 20 seconds (paused when the tab is hidden).
 *
 * Usage:
 *   useDataVersion('solar-project', () => refetchData(), { toastLabel: 'Solar data' });
 */
import { useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const DEFAULT_INTERVAL_MS = 20_000;

export function useDataVersion(kind, onChange, { toastLabel, intervalMs } = {}) {
  const lastRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function tick() {
      if (document.hidden) return;
      try {
        const { data } = await axios.get(`${API}/data-version`, { withCredentials: true });
        const v = data?.[kind] ?? null;
        if (lastRef.current === null) {
          lastRef.current = v;
          return;
        }
        if (v !== lastRef.current) {
          lastRef.current = v;
          if (!cancelled) {
            onChange?.(v);
            if (toastLabel) toast(`${toastLabel} was just updated — refreshing…`);
          }
        }
      } catch {
        /* ignore transient network errors */
      }
    }

    tick(); // seed lastRef
    timerRef.current = setInterval(tick, intervalMs || DEFAULT_INTERVAL_MS);
    return () => { cancelled = true; if (timerRef.current) clearInterval(timerRef.current); };
  }, [kind]);
}

export default useDataVersion;
